import { Logger } from 'aws-amplify';

const logger = new Logger('useMedicalRecordFormatter');

export const useMedicalRecordFormatter = () => {
  /**
   * value, keyからキー名を返す
   * @param value
   * @param key
   */
  const mapKeys = (value, key) => {
    return toJapanese(key);
  };

  /**
   * キーの一覧
   * @returns {string[]}
   */
  const fields = Object.keys(keyToJapaneseMap);

  /**
   * CSVダウンロード用にフォーマットする
   * @param items
   * @returns {*}
   */
  const toCsvDownload = (items) => {
    // 各要素を1階層化する
    const flattenItems = items.map(item => flattenObject(item));

    // 1レコード目の要素に必要な要素が足りなければ追加する=>1レコード目でCSVのヘッダーが決定するため
    if (flattenItems.length > 0) {
      addNeedsFields(flattenItems[0]);
    }

    // 改行コードを除去する
    return replaceNewlineCharacter(flattenItems);
  };

  return {
    mapKeys,
    fields,
    toCsvDownload,
  };
};

/**
 * ネストされたオブジェクトを1階層にする
 * @param obj
 * @returns {{}}
 */
function flattenObject(obj) {
  logger.info('flattenObject', obj);
  let result = {};
  for (const [key, value] of Object.entries(obj)) {
    // eslint-disable-next-line no-prototype-builtins
    if (!obj.hasOwnProperty(key)) {
      continue;
    }

    // Arrayの場合、,区切りで設定する
    if (Array.isArray(value)) {
      result[key] = value.join(',');
      continue;
    }

    if ((typeof value) === 'object' && value !== null) {
      let flatObject = flattenObject(value);
      for (const x in flatObject) {
        // eslint-disable-next-line no-prototype-builtins
        if (!flatObject.hasOwnProperty(x)) {
          continue;
        }

        // ネストした場合にparent.child.valueの形式にする
        result[key + '.' + x] = flatObject[x];
      }
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * key => 日本語キーを取得する
 * 辞書に存在しない場合は、そのままkeyが返る
 * @param key
 * @returns {string}
 */
function toJapanese(key) {
  if (key in keyToJapaneseMap) {
    return keyToJapaneseMap[key];
  }
  return key;
}

/**
 * key: englishKey
 * value: japaneseKey
 * @type {{'recordPlan.plan': string, 'intensiveContact.fulPatientContact': string, 'intensiveContact.placeIntensiveContactOther': string, 'userInfo.name': string, 'interview.diarrhea': string, 'recordConsultation.gastrointestinal': string, 'recordAssessment.diagnostic': string, 'riskOfSerious.riskOfSerious': string, treatmentSerialNumber: string, 'intensiveContact.coronaPatientContact': string, 'riskOfSerious.hasDuringTreatment': string, createdAt: string, 'interview.otherThanCough': string, id: string, 'recordConsultation.shortnessOfBreath': string, updatedAt: string, 'riskOfSerious.obesityRisk': string, 'interview.fluVaccinationHistory': string, treatmentConsultationSeq: string, 'interview.coronaVaccineName': string, 'userInfo.telOfParent': string, 'recordAssessment.diagnosticOther': string, 'interview.conjunctivitis': string, 'recordConsultation.pharyngitis': string, 'recordTime.startTime': string, 'interview.symptoms': string, 'recordConsultation.senseOfFatigue': string, 'interview.searchOtherMethod': string, 'interview.senseOfFatigue': string, 'recordTime.doctorName': string, 'travelHistory.departureDate': string, statusCode: string, 'riskOfSerious.pregnancyRisk': string, 'recordPlan.planOther': string, 'interview.nausea': string, 'userInfo.nameOfParent': string, 'interview.vaccinationHistory': string, 'recordTime.implementationDate': string, 'recordConsultation.dehydration': string, 'riskOfSerious.riskOfSeriousOther': string, 'userInfo.certificates': string, 'riskOfSerious.smokingRisk': string, 'interview.coronaVaccinationDate': string, 'recordTime.endTime': string, 'interview.pharyngitis': string, 'travelHistory.destination': string, 'interview.kitName': string, 'travelHistory.travelHistory': string, 'recordAssessment.finding': string, userId: string, 'userInfo.age': string, 'recordConsultation.cough': string, 'riskOfSerious.otherRisk': string, 'travelHistory.entryDate': string, 'interview.dateOfOnset': string, 'recordConsultation.mainSymptom': string, 'interview.cough': string, 'interview.inspectionDate': string, 'userInfo.kana': string, 'interview.fever': string, 'interview.headache': string, 'interview.searchMethod': string, 'intensiveContact.hasIntensiveContact': string, 'recordPlan.prescription': string}}
 */
const keyToJapaneseMap = {
  'id': '事前問診ID',
  'treatmentConsultationSeq': '診察ID(システム一意)',
  'treatmentSerialNumber': '診察ID(組織一意)',
  'userId': 'ユーザーID',
  // userInfo
  'userInfo.name': '氏名',
  'userInfo.kana': 'フリガナ',
  'userInfo.age': '年齢属性',
  'userInfo.nameOfParent': '保護者の名前',
  'userInfo.telOfParent': '保護者の電話番号',
  'userInfo.certificates': '証明証各種',
  // riskOfSerious
  'riskOfSerious.hasDuringTreatment': '重症化リスク要因_既往歴',
  'riskOfSerious.riskOfSerious': '重症化リスク要因_既往歴(病名)',
  'riskOfSerious.riskOfSeriousOther': '重症化リスク要因_既往歴(病名)(その他)',
  'riskOfSerious.obesityRisk': '重症化リスク要因_肥満(BMI30以上)',
  'riskOfSerious.pregnancyRisk': '重症化リスク要因_妊娠',
  'riskOfSerious.smokingRisk': '重症化リスク要因_喫煙歴',
  'riskOfSerious.otherRisk': '重症化リスク要因_その他',
  // interview
  'interview.inspectionDate': '検査_検体採取日',
  'interview.vaccinationHistory': '新型コロナウイルスワクチン_接種歴',
  'interview.coronaVaccinationDate': '新型コロナウイルスワクチン_直近接種年月日',
  'interview.coronaVaccineName': '新型コロナウイルスワクチン_直近接種',
  'interview.fluVaccinationHistory': 'インフルエンザワクチン_1年以内の接種歴',
  'interview.symptoms': '症状の有無',
  'interview.dateOfOnset': '症状が発生した日',
  'interview.fever': '症状_発熱',
  'interview.feverNum': '症状_発熱時の体温',
  'interview.cough': '症状_せき',
  'interview.otherThanCough': '症状_せき以外の呼吸器症状',
  'interview.senseOfFatigue': '症状_倦怠感',
  'interview.headache': '症状_頭痛',
  'interview.nausea': '症状_嘔気/嘔吐',
  'interview.diarrhea': '症状_下痢',
  'interview.conjunctivitis': '症状_結膜炎',
  'interview.pharyngitis': '症状_咽頭痛',

  // intensiveContact
  'intensiveContact.fulPatientContact': '感染の可能性_インフルエンザ患者の方との接触または身近での流行',
  'intensiveContact.coronaPatientContact': '感染の可能性_新型コロナウイルス患者の方との接触または身近での流行',

  'statusCode': 'ステータスコード',

  'createdAt': '作成日時',
  'updatedAt': '更新日時',
};

/**
 * 改行コードを置き換える
 * @param items
 * @param replaceValue
 */
function replaceNewlineCharacter(items, replaceValue = '') {
  return items.map(x => (typeof x === 'string') ? x.replace(/\r?\n/g, replaceValue) : x);
}

/**
 * 必要なフィールドがなければ追加する
 * @param item
 */
function addNeedsFields(item) {
  Object.keys(keyToJapaneseMap).forEach(field => {
    // itemにfieldが存在しなければ追加する
    if (field in item === false) {
      item[field] = '';
    }
  });
}
